import { useEffect, useMemo, useState } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip } from 'chart.js'
import { Line } from 'react-chartjs-2'
import { TrendingUp, Users, Percent, Award } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bReventaService from '@/services/cd2bReventaService'
import cd2bCertificacionService from '@/services/cd2bCertificacionService'
import carteraService from '@/services/carteraService'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { useReventaHabilitada } from '@/hooks/useReventaHabilitada'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import BannerGatingReventa from '@/components/cd2b/BannerGatingReventa'
import KpiCard from '@/components/KpiCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import DataTable from '@/components/common/DataTable'
import { useTemaGraficas } from '@/lib/temaGraficas'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip)

const NIVEL_LABEL = { aprendiz: 'Aprendiz', certificado: 'Certificado', especialista: 'Especialista', maestro: 'Maestro' }
const TRACK_LABEL = { ventas: 'Ventas', implementacion: 'Implementación', fiscal_sv: 'Fiscal El Salvador', soporte: 'Soporte' }
const ESTADO_LIQ = {
  pendiente: { label: 'Pendiente', variant: 'warning' },
  aprobada: { label: 'Aprobada', variant: 'success' },
  en_pago: { label: 'En pago', variant: 'default' },
  pagada: { label: 'Pagada', variant: 'success' },
  retenida: { label: 'Retenida · verificar', variant: 'danger' },
  anulada: { label: 'Anulada', variant: 'secondary' },
}
const mesLabel = (d) => new Date(d).toLocaleDateString('es-SV', { month: 'short', year: '2-digit' })

/**
 * Resumen (docs/120 §7.1) — pantalla nueva, NO el Dashboard genérico existente: el Dashboard es
 * compartido con partners que no son consultores cd-2b (referidos, comisión fija) y este resumen
 * es enteramente sobre el modelo de reventa (saldo margen+comisión unificado, insignia, margen
 * ganado). Mezclarlo ahí habría condicionado medio Dashboard a `esConsultorCd2b`.
 */
export default function ReventaResumen() {
  const habilitado = useReventaHabilitada()
  const tema = useTemaGraficas()
  const [cargando, setCargando] = useState(true)
  const [saldo, setSaldo] = useState(null)
  const [politica, setPolitica] = useState(null)
  const [insignias, setInsignias] = useState([])
  const [liquidaciones, setLiquidaciones] = useState([])
  const [cartera, setCartera] = useState([])

  useEffect(() => {
    let vivo = true
    setCargando(true)
    Promise.all([
      cd2bReventaService.getSaldo().catch(() => null),
      cd2bReventaService.getPolitica().catch(() => null),
      cd2bCertificacionService.misInsignias().catch(() => null),
      cd2bReventaService.listarLiquidaciones({ limit: 50 }).catch(() => null),
      carteraService.getCartera().catch(() => null),
    ]).then(([s, p, ins, liq, cart]) => {
      if (!vivo) return
      setSaldo(s?.data || null)
      setPolitica(p?.data || null)
      setInsignias(ins?.data?.insignias || [])
      setLiquidaciones(liq?.data?.liquidaciones || [])
      setCartera(cart?.data?.items || [])
    }).catch(() => { if (vivo) toast.error('No se pudo cargar el resumen') })
      .finally(() => { if (vivo) setCargando(false) })
    return () => { vivo = false }
  }, [])

  // La insignia "más relevante" para el KPI: la vigente de mayor nivel, o la primera si ninguna
  // está vigente (para que "por vencer" también se muestre en vez de desaparecer del resumen).
  const insigniaDestacada = useMemo(() => {
    const vigentes = insignias.filter((i) => i.estado === 'vigente' || i.estado === 'por_vencer')
    return vigentes[0] || insignias[0] || null
  }, [insignias])

  const nombrePorSub = useMemo(() => {
    const m = new Map()
    for (const c of cartera) m.set(String(c.subscriptionId), c)
    return m
  }, [cartera])

  // Margen ganado por mes (últimos 6 meses con liquidaciones no anuladas) — el mismo dato que
  // alimenta la tabla de abajo, solo agregado por mes en vez de por cliente.
  const chart = useMemo(() => {
    const activas = liquidaciones.filter((l) => l.estado !== 'anulada')
    const porMes = new Map()
    for (const l of activas) {
      const key = new Date(l.createdAt).toISOString().slice(0, 7)
      porMes.set(key, (porMes.get(key) || 0) + (l.montos?.margenCents || 0))
    }
    const claves = [...porMes.keys()].sort().slice(-6)
    return {
      labels: claves.map((k) => mesLabel(`${k}-01`)),
      data: claves.map((k) => r2(porMes.get(k) / 100)),
    }
  }, [liquidaciones])

  const ultimosClientes = useMemo(() => liquidaciones.slice(0, 5), [liquidaciones])
  const clientesActivos = cartera.filter((c) => !c.esDemo).length

  const cifra = (cents, moneda) => fmtMoneda((cents || 0) / 100, moneda)
  const columnasClientes = [
    {
      clave: 'cliente', titulo: 'Cliente', prioridad: 'titulo',
      celda: (l) => {
        const c = nombrePorSub.get(String(l.subscriptionId))
        return (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{c?.empresaNombre || 'Empresa'}</p>
            <p className="text-caption text-subtle">{c?.edicion || ''}</p>
          </div>
        )
      },
    },
    { clave: 'costo', titulo: 'Tu costo', alineacion: 'derecha', prioridad: 'meta', celda: (l) => <span className="font-mono tabular-nums text-foreground">{cifra(l.montos?.baseCents, l.montos?.currency)}</span> },
    {
      clave: 'margen', titulo: 'Tu margen', alineacion: 'derecha', prioridad: 'valor',
      celda: (l) => <span className="font-mono font-semibold tabular-nums text-accent-700 dark:text-accent-300">+{cifra(l.montos?.margenCents, l.montos?.currency)}</span>,
    },
    { clave: 'cobrado', titulo: 'Paga el cliente', alineacion: 'derecha', prioridad: 'meta', celda: (l) => <span className="font-mono tabular-nums text-foreground">{cifra(l.montos?.cobradoCents, l.montos?.currency)}</span> },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (l) => {
        const est = ESTADO_LIQ[l.estado] || { label: l.estado, variant: 'secondary' }
        return <Badge variant={est.variant}>{est.label}</Badge>
      },
    },
  ]

  if (cargando) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader title="Resumen" description="Tu negocio de reventa cd-2b: saldo, cartera y margen ganado." />

      {!habilitado && <BannerGatingReventa variante="resumen" />}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Saldo pendiente"
          value={fmtMoneda((saldo?.totalPendienteCents || 0) / 100)}
          icon={<TrendingUp size={20} />}
          tone="accent"
          delta={fmtMoneda((saldo?.margenPendienteCents || 0) / 100)}
          deltaLabel="de margen de reventa"
        />
        <KpiCard
          title="Clientes activos"
          value={clientesActivos}
          icon={<Users size={20} />}
          tone="primary"
          delta={cartera.length - clientesActivos || 0}
          deltaLabel="empresas demo/práctica"
        />
        <KpiCard
          title="Margen global configurado"
          value={`${politica?.margenGlobalPct ?? 0}%`}
          icon={<Percent size={20} />}
          tone="accent"
          delta={`Tope vigente: ${politica?.topeVigentePct ?? 0}%`}
        />
        {habilitado && insigniaDestacada ? (
          <KpiCard
            title="Insignia vigente"
            value={
              <Badge variant={insigniaDestacada.estado === 'vigente' ? 'accent' : 'warning'}>
                {NIVEL_LABEL[insigniaDestacada.nivel] || insigniaDestacada.nivel}
              </Badge>
            }
            icon={<Award size={20} />}
            tone="success"
            delta={TRACK_LABEL[insigniaDestacada.track] || insigniaDestacada.track}
            deltaLabel={insigniaDestacada.expiraAt ? `vence ${new Date(insigniaDestacada.expiraAt).toLocaleDateString('es-SV')}` : ''}
          />
        ) : (
          <KpiCard
            title="Insignia"
            value={<Badge variant="secondary">Sin certificar</Badge>}
            icon={<Award size={20} />}
            tone="neutral"
            delta="Solo modo demo habilitado"
          />
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-h3">Margen ganado, últimos meses</CardTitle>
          <p className="text-body-sm text-subtle">
            Lo que te ha correspondido a ti (margen), separado de lo que retiene CoreDesk (precio base).
          </p>
        </CardHeader>
        <CardContent>
          {chart.data.length === 0 ? (
            <p className="py-6 text-center text-body-sm text-subtle">
              Todavía no tienes liquidaciones de margen — aparecerán aquí en cuanto se pague tu primer cliente en modo reventa.
            </p>
          ) : (
            <div className="h-48">
              {/* `key` por tema: Chart.js pinta en canvas y no se entera solo del cambio de tema. */}
              <Line
                key={tema.isDark ? 'oscuro' : 'claro'}
                data={{
                  labels: chart.labels,
                  datasets: [{
                    data: chart.data,
                    borderColor: tema.linea,
                    backgroundColor: tema.relleno,
                    pointBackgroundColor: tema.linea,
                    pointBorderColor: tema.linea,
                    tension: 0.35,
                    fill: true,
                  }],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: tema.tooltipBg,
                      titleColor: tema.tooltipText,
                      bodyColor: tema.tooltipText,
                      callbacks: { label: (ctx) => fmtMoneda(ctx.parsed.y) },
                    },
                  },
                  scales: {
                    x: { grid: { color: tema.grid }, border: { color: tema.grid }, ticks: { color: tema.ticks } },
                    y: { grid: { color: tema.grid }, border: { color: tema.grid }, ticks: { color: tema.ticks, callback: (v) => fmtMoneda(v) } },
                  },
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-h3">Tus últimos clientes</CardTitle>
          <p className="text-body-sm text-subtle">
            Vista rápida costo / margen / precio final — el detalle completo está en la ficha de cada cliente.
          </p>
        </CardHeader>
        <CardContent>
          {ultimosClientes.length === 0 ? (
            <p className="py-4 text-body-sm text-subtle">Todavía no tienes liquidaciones registradas.</p>
          ) : (
            <DataTable
              etiquetaTabla="Tus últimos clientes"
              columnas={columnasClientes}
              filas={ultimosClientes}
              claveDeFila={(l) => l._id}
              tarjetasEnMovil
            />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}

function r2(n) { return Math.round((Number(n) || 0) * 100) / 100 }
